import React, { useEffect, useMemo, useRef, useState } from 'react'
import trash from '../../../../../assets/restaurant/images/trash.svg';
import { useTranslation } from 'react-i18next';
import { ITEM_TYPES, LOADER_TIMEOUT_TIME, SWAL_SETTINGS } from '../../../../../utils/Constants';
import Swal from 'sweetalert2';
import * as addonService from '../../../services/addons.service';
import * as menuItemsService from '../../../services/menuItem.services';
import { getTranslatedKey, globalLoader, handleOffcanvasScroll, handleServerValidations, useDebouncing } from '../../../../../utils/commonfunction';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import RecordNotFound from '../../../shared/recordNotFound';

export default function OptionsListing() {
    const navigate = useNavigate();
    const params = useParams();
    const addFormCloseBtn = useRef();
    const editFormCloseBtn = useRef();
    const assignFormCloseBtn = useRef();
    const { t } = useTranslation();
    const [search] = useState({});
    const [globalSearch, setGlobalSearch] = useState('');
    const [refreshList, setRefreshList] = useState(false);
    const [refreshItemsList, setRefreshItemsList] = useState(null);
    const [loader, setLoader] = useState(true);
    const [itemsLoader, setItemsLoader] = useState(true);
    const [List, setList] = useState([]);
    const [editableId, setEditableId] = useState(null);
    const [activeOption, setActiveOption] = useState(null);
    const [addSubmissionLoader, setAddSubmissionLoader] = useState(false);
    const [editSubmissionLoader, setEditSubmissionLoader] = useState(false);
    const [assignSubmissionLoader, setAssignSubmissionLoader] = useState(false);

    /** Add record form */
    const addForm = useFormik({
        initialValues: {
            addon_name: "",
        },
        validationSchema: Yup.object().shape({
            addon_name: Yup.string().trim().required(t("Option is required")).max(20, "Option cannot be more than 20 characters"),

        }),
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            setAddSubmissionLoader(true);
            addonService.add(values).then((response) => {
                setAddSubmissionLoader(false);
                if (response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    resetForm();
                    addFormCloseBtn?.current?.click();
                    setRefreshList(!refreshList);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch((error) => {
                setAddSubmissionLoader(false);
                console.error("Error updating profile", error);
            });
        },
    });
    /** Edit record form */
    const editForm = useFormik({
        initialValues: {
            o_id: "",
            addon_name: "",
        },
        validationSchema: Yup.object().shape({
            addon_name: Yup.string().trim().required(t("Option is required")).max(20, "Option cannot be more than 20 characters"),
        }),
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            setEditSubmissionLoader(true);
            addonService.edit(values).then((response) => {
                setEditSubmissionLoader(false);
                if (response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    resetForm();
                    editFormCloseBtn?.current?.click();
                    setRefreshList(!refreshList);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch((error) => {
                setEditSubmissionLoader(false);
                console.error("Error updating profile", error);
            });
        },
    });

    const assignForm = useFormik({
        initialValues: {
            o_id: activeOption?._id,
            // o_id: params?.active_option,
            assigned_items: "",
        },
        validationSchema: Yup.object().shape({
            assigned_items: Yup.string().trim().required(t("Product value is required")).max(30, "Product value cannot be more than 30 characters"),
        }),
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            setAssignSubmissionLoader(true);
            const formData = {
                o_id: values.o_id,
                assigned_items: values.assigned_items
            };
            addonService.assignItems(formData).then((response) => {
                setAssignSubmissionLoader(false);
                if (response.success) {
                    Swal.fire({
                        icon: "success",
                        text: 'Value created successfully',
                        ...SWAL_SETTINGS,
                    });
                    resetForm();
                    assignFormCloseBtn?.current?.click();
                    setRefreshList(!refreshList);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch((error) => {
                setAssignSubmissionLoader(false);
                console.error("Error updating profile", error);
            });
        },
    });

    const prepareGlobalSearch = (value) => {
        setGlobalSearch(value);
    };
    const searchDebounce = useDebouncing(prepareGlobalSearch);

    const handleActiveOption = (isDefault, data) => {
        if (isDefault) {
            if (params.active_option) {
                const activeRecord = data.filter(item => item?._id === params.active_option)[0];
                if (activeRecord) {
                    setActiveOption(activeRecord);
                } else {
                    setActiveOption(data[0] || null);
                }
            } else {
                setActiveOption(data[0] || null);
            }
        } else {
            setActiveOption(data || null);
        }
    };

    const isSelectedItem = (record) => {
        if (activeOption?.assigned_items?.length) {
            return Boolean(activeOption.assigned_items.filter(item => item?._id === record?._id)[0]);
        }
        return false;
    };

    /** Getting list */
    useEffect(() => {
        setLoader(true);
        const formData = new FormData();
        if (Object.keys(search).length) {
            formData.append("search", JSON.stringify(search));
        }
        formData.append("global_search", globalSearch);
        addonService.list(formData).then(response => {
            setTimeout(() => { setLoader(false); }, LOADER_TIMEOUT_TIME);
            if (response.success) {
                setList(response && response.data && response.data.list ? response.data.list : []);
                handleActiveOption(true, response.data.list);
            } else {
                Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                });
            }
        }).catch(error => {
            setTimeout(() => { setLoader(false); }, LOADER_TIMEOUT_TIME);
            console.log("error ====> ", error);
        });
    }, [search, refreshList, globalSearch]);

    /** Getting record details for edit */
    useEffect(() => {
        if (editableId) {
            globalLoader(true);
            addonService.details(editableId).then(response => {
                globalLoader(false);
                if (response.success) {
                    editForm.setValues({
                        o_id: response?.data?._id,
                        addon_name: response?.data?.addon_name
                    });
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                }
            }).catch(error => {
                globalLoader(false);
            });
        }
    }, [editableId]);


    /** Handling the language change */
    useMemo(() => {
        document.addEventListener('language_change', function () {
            setRefreshList(!refreshList);
        });
    });

    /** Handling the delete functionality */
    const handleDelete = (record) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("Do you really want to delete this record? This process cannot be undone."),
            imageUrl: trash,
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: t("Delete"),
            cancelButtonText: t("Cancel"),
            reverseButtons: true,
            customClass: {
                popup: 'swal-restaurant',
                confirmButton: 'btn btn-danger col',
                cancelButton: 'btn btn-secondary col'
            },
            allowOutsideClick: false
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                addonService.deleteRecord({ o_id: record._id }).then((response) => {
                    globalLoader(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        setRefreshList(!refreshList);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                }).catch(() => {
                    globalLoader(false);
                });
            }
        });
    };
    /** Handling the delete functionality */
    const handleDeleteFromAssigned = (id, record) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("Do you really want to delete this record? This process cannot be undone."),
            imageUrl: trash,
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: t("Delete"),
            cancelButtonText: t("Cancel"),
            reverseButtons: true,
            customClass: {
                popup: 'swal-restaurant',
                confirmButton: 'btn btn-danger col',
                cancelButton: 'btn btn-secondary col'
            },
            allowOutsideClick: false
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                addonService.deleteAssignedItem({ option_id: activeOption?._id, item : record }).then((response) => {
                    globalLoader(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        setRefreshList(!refreshList);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                }).catch(() => {
                    globalLoader(false);
                });
            }
        });
    };

    const selectItemsToAssign = (e, i) => {
        let allValues = [...assignForm.values?.assigned_items];
        allValues[i].checked = e.target.checked
        assignForm.setFieldValue(`assigned_items`, allValues);
    };
    const changeActiveOption = (e, record) => {
        if (e.target.classList.contains("active_option_handler")) {
            navigate('/restaurant/menu/options/list/' + record?._id);
            handleActiveOption(false, record);
            setRefreshItemsList(!refreshItemsList);
        }
    };

    return (
        <main id='main-content'>
            <div className='inner-container'>
                <div className='page-header d-flex align-items-center'>
                    <h4 className='heading-title-text'>{t('Options')}</h4>
                </div>
                <div className='card-outer-header d-flex justify-content-between align-items-center mb-4'>
                    <div className='search-box'>
                        <input type='text' className='form-control search-input' placeholder={t("Search")} onInput={(e) => {
                            searchDebounce(e.target.value);
                        }} />
                    </div>
                    <div className='card-head-group d-flex align-items-center gap-2'>
                        <button className='btn btn-primary' data-bs-toggle="offcanvas" data-bs-target="#add-form-canvas"> {t('Add New Option')}</button>
                    </div>
                </div>
                <div className='option-item-wrap d-flex'>
                    <div className='option-item-list'>
                        <div className='card'>
                            <div className='card-body'>
                                {
                                    loader ?
                                        <div className='w-100'>
                                            <Skeleton className='mb-2' height={50} count={5} />
                                        </div>
                                        :
                                        List.length ?
                                            <ul className='option-list-ul'>
                                                {List.map((item, index) => {
                                                    return (<li key={index} className={'active_option_handler cp' + (activeOption?._id === item?._id ? " active" : "")} onClick={(e) => { changeActiveOption(e, item) }}>
                                                        {/* {console.log("item", item)} */}
                                                        <span className='active_option_handler'>{getTranslatedKey('addon_name', item)}</span>
                                                        {/* {item?.addon_name} */}
                                                        <div className='option-btns d-flex gap-2'>
                                                            <button className='btn btn-icon' data-bs-toggle="offcanvas" data-bs-target="#edit-form-canvas" onClick={() => { setEditableId(item?._id) }}>
                                                                <i className='ti ti-edit'></i>
                                                            </button>
                                                            <button className='btn btn-icon' onClick={() => { handleDelete(item) }}>
                                                                <i className='ti ti-trash'></i>
                                                            </button>
                                                        </div>
                                                    </li>)
                                                })}
                                            </ul>
                                            :
                                            <RecordNotFound heading="No options found" />
                                }
                            </div>
                        </div>
                    </div>
                    <div className='option-item-info'>
                        <div className='card'>
                            <div className='card-body'>
                                {
                                    loader ?
                                        <div className='w-100'>
                                            <Skeleton className='mb-2' height={50} count={5} />
                                        </div>
                                        :
                                        activeOption?.assigned_items?.length ?
                                            <div className='table-responsive' style={{ minHeight: 'auto' }}>
                                                <table className='table'>
                                                    <thead>
                                                        <tr>
                                                            <th>{t('Product name')}</th>
                                                            <th style={{ width: "150px" }}>{t('Action')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {activeOption?.assigned_items.map((item, index) => {
                                                            return (<tr key={index}>
                                                                <td>{item}</td>
                                                                {/* <td>{getTranslatedKey('item_name', item)}</td> */}
                                                                <td>
                                                                    <button className='btn btn-icon' onClick={() => { handleDeleteFromAssigned(activeOption?._id, item) }}><i className="ti ti-trash"></i></button>
                                                                </td>
                                                            </tr>)
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            :
                                            <RecordNotFound heading='No values found' />
                                }
                                {
                                    List.length && !loader ?
                                        <div className='text-center mt-4'>
                                            <button className='btn btn-primary 123' data-bs-toggle="offcanvas" data-bs-target="#assign-items-canvas"
                                                onClick={() => { setRefreshItemsList(!refreshItemsList) }}>{t('Create Values')}</button>
                                        </div>
                                        : null
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add form  */}
            <div className="offcanvas offcanvas-end right-modal-w" id="add-form-canvas" data-bs-backdrop="static" tabIndex="-1" aria-labelledby="staticBackdropLabel">
                <div className="offcanvas-header">
                    <h3 className="offcanvas-title" id="offcanvasRightLabel">{t("Add new option")}</h3>
                    <button type="button" className="btn btn-icon ms-auto" ref={addFormCloseBtn} data-bs-dismiss="offcanvas" aria-label="Close" onClick={() => { addForm.resetForm() }}>
                        <i className="ti ti-x"></i>
                    </button>
                </div>
                <div className="offcanvas-body">
                    <form onSubmit={addForm.handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">{t("Option Name")}<span>*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("Enter option name")}
                                name="addon_name"
                                value={addForm.values.addon_name}
                                onChange={addForm.handleChange}
                                onBlur={addForm.handleBlur}
                            />
                            {addForm.touched.addon_name && addForm.errors.addon_name ? (
                                <div className="text-danger">{t(addForm.errors.addon_name)}</div>
                            ) : null}
                        </div>

                        <div className="button-form">
                            <button type="submit" className={"btn btn-primary w-100" + (addSubmissionLoader ? " btn-loader" : "")}>
                                {t("Submit")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Edit form  */}
            <div className="offcanvas offcanvas-end right-modal-w" id="edit-form-canvas" data-bs-backdrop="static" tabIndex="-1" aria-labelledby="staticBackdropLabel">
                <div className="offcanvas-header">
                    <h3 className="offcanvas-title" id="offcanvasRightLabel">{t("Edit option")}</h3>
                    <button type="button" className="btn btn-icon ms-auto" ref={editFormCloseBtn} data-bs-dismiss="offcanvas" aria-label="Close" onClick={() => { editForm.resetForm(); setEditableId(null); }}>
                        <i className="ti ti-x"></i>
                    </button>
                </div>
                <div className="offcanvas-body">
                    <form onSubmit={editForm.handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">{t("Option Name")}<span>*</span></label>
                            <input type="hidden" name="o_id" value={editForm?.values?.o_id} onChange={editForm.handleChange} />
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("Enter option name")}
                                name="addon_name"
                                value={editForm.values.addon_name}
                                onChange={editForm.handleChange}
                                onBlur={editForm.handleBlur}
                            />
                            {editForm.touched.addon_name && editForm.errors.addon_name ? (
                                <div className="text-danger">{t(editForm.errors.addon_name)}</div>
                            ) : null}
                        </div>

                        <div className="button-form">
                            <button type="submit" className={"btn btn-primary w-100" + (editSubmissionLoader ? " btn-loader" : "")}>
                                {t("Update")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

          

            <div className="offcanvas offcanvas-end right-modal-w" id="assign-items-canvas" data-bs-backdrop="static" tabIndex="-1" aria-labelledby="staticBackdropLabel">
                <div className="offcanvas-header">
                    <h3 className="offcanvas-title 12" id="offcanvasRightLabel">{t("Create values")}</h3>
                    <button type="button" className="btn btn-icon ms-auto" ref={assignFormCloseBtn} data-bs-dismiss="offcanvas" aria-label="Close" onClick={() => { assignForm.resetForm(); }}>
                        <i className="ti ti-x"></i>
                    </button>
                </div>
                <div className="offcanvas-body">
                    <form onSubmit={assignForm.handleSubmit}>
                        <div className="mb-3">
                        <label className="form-label">{t("Product Name")}<span>*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("Enter Product value")}
                                name="assigned_items"
                                value={assignForm.values.assigned_items}
                                onChange={assignForm.handleChange}
                                onBlur={assignForm.handleBlur}
                            />
                            {assignForm.touched.assigned_items && assignForm.errors.assigned_items ? (
                                <div className="text-danger">{t(assignForm.errors.assigned_items)}</div>
                            ) : null}
                        </div>

                        <div className="button-form">
                            <button type="submit" className={"btn btn-primary w-100" + (addSubmissionLoader ? " btn-loader" : "")}>
                                {t("Submit")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </main>
    )
}
